import { useMemo, useState } from "react";
import {
  disableFoodOsmPoiSource,
  enableFoodOsmPoiSource,
  fetchFoodOsmPoiSourceEnablementStatus,
  fetchFoodOsmPoiSourceIngestionReadiness,
  runFoodOsmPoiSourceIngestion,
} from "../../../../api/foodApi";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";
import { useAuth } from "../../../../auth/useAuth";
import { foodSourceGroupLabel } from "../../../../constants/foodSourceConstants";
import FoodSourceControlsGrid from "../dataSources/FoodSourceControlsGrid";
import {
  getOsmPoiRunResultMessage,
  OSM_POI_INGESTION_HREF,
  parseOsmPoiApiError,
} from "./foodOsmPoiSourceControlsHelpers";

function normalizeHealth(source) {
  if (!source.isActive) {
    return "disabled";
  }

  const status = String(source.healthStatus || "").toLowerCase();
  if (status === "running") return "running";
  if (status === "failing" || status === "failed") return "failed";
  if (status === "stale" || status === "warning") return "warning";
  if (status === "healthy") return "healthy";
  if (status === "discovery_pending" || status === "not_configured") return "never_run";
  return status || "never_run";
}

const FoodOsmPoiSourceControlsTable = ({
  sources,
  filters,
  onRefresh,
  showToast,
  onSourcePatch,
  hideEmpty = false,
}) => {
  const [busyCode, setBusyCode] = useState(null);
  const [runningCode, setRunningCode] = useState(null);
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManageSources =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const filteredSources = useMemo(() => {
    let list = [...(sources || [])];

    if (filters.search) {
      const query = filters.search.toLowerCase();
      list = list.filter(
        (source) =>
          source.sourceName?.toLowerCase().includes(query) ||
          source.sourceCode?.toLowerCase().includes(query) ||
          foodSourceGroupLabel(source.sourceGroup)?.toLowerCase().includes(query)
      );
    }

    if (filters.enabledFilter === "enabled") {
      list = list.filter((source) => source.isActive);
    } else if (filters.enabledFilter === "disabled") {
      list = list.filter((source) => !source.isActive);
    }

    if (filters.statusFilter && filters.statusFilter !== "all") {
      list = list.filter((source) => normalizeHealth(source) === filters.statusFilter);
    }

    return list.sort((a, b) => a.sourceName.localeCompare(b.sourceName));
  }, [sources, filters]);

  if (hideEmpty && !filteredSources.length) {
    return null;
  }

  const patchSource = (sourceCode, patch) => {
    onSourcePatch?.(sourceCode, patch);
  };

  const handleToggleActive = async (source) => {
    if (!canManageSources) return;

    const nextActive = !source.isActive;
    setBusyCode(source.sourceCode);

    try {
      if (nextActive) {
        const enablement = await fetchFoodOsmPoiSourceEnablementStatus(source.sourceCode);
        if (!enablement.canEnable) {
          showToast(
            enablement.blockingReasons?.[0] || `${source.sourceName} cannot be enabled yet.`,
            "error"
          );
          return;
        }

        const confirmed = window.confirm(
          `Enable ${source.sourceName} for OSM food POI collection?`
        );
        if (!confirmed) return;

        const result = await enableFoodOsmPoiSource(source.sourceCode, {
          confirm: true,
          enabledBy: "admin",
          notes: "Enabled from Source Controls.",
        });
        patchSource(source.sourceCode, {
          isActive: true,
          healthStatus: "healthy",
          activationNote: null,
        });
        showToast(result.message || `${source.sourceName} enabled.`, "success");
      } else {
        const reason = window.prompt(
          `Reason for disabling ${source.sourceName}:`,
          "Disabled from Source Controls."
        );
        if (!reason?.trim()) return;

        const result = await disableFoodOsmPoiSource(source.sourceCode, {
          confirm: true,
          disabledBy: "admin",
          reason: reason.trim(),
        });
        patchSource(source.sourceCode, {
          isActive: false,
          healthStatus: "disabled",
        });
        showToast(result.message || `${source.sourceName} disabled.`, "success");
      }

      onRefresh?.({ silent: true });
    } catch (error) {
      showToast(parseOsmPoiApiError(error), "error");
    } finally {
      setBusyCode(null);
    }
  };

  const handleRunNow = async (source) => {
    if (!canManageSources) return;

    setBusyCode(source.sourceCode);
    setRunningCode(source.sourceCode);

    try {
      const readiness = await fetchFoodOsmPoiSourceIngestionReadiness(source.sourceCode);
      const dryRun = !source.isActive;

      if (dryRun && readiness.dryRunAllowed === false) {
        showToast(
          readiness.blockingReasons?.[0] || `${source.sourceName} is not runnable yet.`,
          "error"
        );
        return;
      }

      if (!dryRun) {
        if (readiness.realRunAllowed === false) {
          showToast(
            readiness.blockingReasons?.[0] || `${source.sourceName} cannot run real ingestion yet.`,
            "error"
          );
          return;
        }

        const confirmed = window.confirm(
          `Run OSM food POI ingestion for ${source.sourceName}? This will upsert food stores${
            readiness.useMockDefault ? " using the mock fixture" : " from Overpass"
          }.`
        );
        if (!confirmed) return;
      }

      const result = await runFoodOsmPoiSourceIngestion(source.sourceCode, {
        dryRun,
        useMock: readiness.useMockDefault,
      });

      showToast(
        getOsmPoiRunResultMessage(result) || `Run completed for ${source.sourceName}.`,
        "success"
      );
      onRefresh?.({ silent: true });
    } catch (error) {
      showToast(parseOsmPoiApiError(error), "error");
    } finally {
      setBusyCode(null);
      setRunningCode(null);
    }
  };

  const handleOpenIngestion = () => {
    window.location.hash = OSM_POI_INGESTION_HREF;
  };

  if (!filteredSources.length) {
    return null;
  }

  return (
    <>
      <h4 className="admin-source-controls-dash__type-subheading">OSM food POI sources</h4>
      <FoodSourceControlsGrid
        sources={filteredSources}
        emptyMessage="No OSM food POI sources match the current filters."
        canManageSources={canManageSources}
        busyCode={busyCode}
        runningCode={runningCode}
        onToggleEnabled={handleToggleActive}
        onRunNow={handleRunNow}
        onViewRuns={handleOpenIngestion}
        resolveRunNowTitle={(source) =>
          !source.isActive
            ? "Run a dry-run validation (enable source for real collection)"
            : runningCode === source.sourceCode
              ? "Ingestion run in progress"
              : "Run OSM food POI ingestion now"
        }
        onRefresh={onRefresh}
        showToast={showToast}
        onSourcePatch={onSourcePatch}
      />
    </>
  );
};

export default FoodOsmPoiSourceControlsTable;
